import React, { useState, useEffect, useCallback } from "react";
import { Spin, Input, Checkbox, Button, notification, message, Tooltip } from "antd";
import { useFetch } from "../../../../utils/hooks";
import { getIcon } from "../../../../utils/images";
import { getShareUrl } from "../../../../services/user";
import { getShipmentDetailsWatcher, updateWatchers, deleteWatcher } from "../../../../services/shipments";
import {useCopyToClipboard} from "usehooks-ts";
import "./WatcherPane.scss";

const WatcherPane = ({ shipment }) => {
  // State to store the existing watchers and new watcher input
  const [watchers, setWatchers] = useState({});
  const [value, copy] = useCopyToClipboard()
  const [newWatcherEmail, setNewWatcherEmail] = useState("");
  const [newWatcherAttributes, setNewWatcherAttributes] = useState({
    updates: true,
    documents: false,
  });

  // Fetch existing watchers
  const { data, loading } = useFetch(useCallback(() => getShipmentDetailsWatcher(shipment.id), [shipment.id]));

  useEffect(() => {
    if (data?.watchers) {
      setWatchers(data.watchers); // Assuming `data.watchers` is the current watcher list
    }
  }, [data]);

  // Function to send notifications
  const openNotification = (type, message) => {
    notification[type]({
      message: message,
    });
  };

  // Handle adding a new watcher
  const handleAddWatcher = async () => {
    if (!newWatcherEmail) {
      openNotification("error", "Please enter an email address");
      return;
    }

    // Validate the email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newWatcherEmail)) {
      openNotification("error", "Invalid email format. Please enter only one valid email.");
      return;
    }

    const updatedWatchers = {
      ...watchers,
      [newWatcherEmail]: { ...newWatcherAttributes },
    };

    setWatchers(updatedWatchers);

    try {
      await updateWatchers(shipment.id, updatedWatchers);
      openNotification("success", "Access shared successfully!");
    } catch (error) {
      console.error("Error updating share access:", error);
      openNotification("error", "Failed to share access.");
    }

    // Reset new watcher input fields
    setNewWatcherEmail("");
    setNewWatcherAttributes({ updates: true, documents: false });
  };

  // Handle watcher removal
  const handleRemoveWatcher = async (email) => {
    const updatedWatchers = { ...watchers };
    delete updatedWatchers[email];
    setWatchers(updatedWatchers);
    try {
      await deleteWatcher(shipment.id, {email: email});
      openNotification("success", "Share access removed successfully!");
    } catch (error) {
      console.error("Error removing share access:", error);
      openNotification("error", "Failed to remove share access.");
    }
  };

  // Handle Enter key press in email input to add watcher
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddWatcher();
    }
  };

  async function handleShareShipment() {
    const resp = await getShareUrl({ shipment_id: shipment.id })
    copy(`${process.env.REACT_APP_ROOT_HOST}${resp.data.url}`)
    message.success('Share URL copied to clipboard');
  }

  if (loading) {
    return <Spin size="large" className="spinner" />;
  }

  return (
    <div className="WatcherPane pane">
      <div className="share-icon">
        <Tooltip title="Click to copy share URL">
          <img src={getIcon('share-shipment')} onClick={handleShareShipment} alt="Share Shipment" />
        </Tooltip>
      </div>
      <div className="pane__header">
        <Tooltip title="Click to copy share URL">
          <img src={getIcon('share-shipment')} onClick={handleShareShipment} alt="Share Shipment" />
        </Tooltip>

        <div className="pane__title">Share Access</div>
      </div>

      <div className="pane__wrapper">

        {/* Add new watcher section */}
        <h3>Share New Access</h3>
        <div id="add-watcher-container">
          <div>
            <Input
              placeholder="Enter email"
              value={newWatcherEmail}
              onChange={(e) => setNewWatcherEmail(e.target.value)}
              onKeyPress={handleKeyPress} // Add watcher when pressing Enter
            />
            <Checkbox
              checked={newWatcherAttributes.updates}
              onChange={(e) => setNewWatcherAttributes({ ...newWatcherAttributes, updates: e.target.checked })}
            >
              Updates
            </Checkbox>
            <Checkbox
              checked={newWatcherAttributes.documents}
              onChange={(e) => setNewWatcherAttributes({ ...newWatcherAttributes, documents: e.target.checked })}
            >
              Documents
            </Checkbox>
          </div>
          <Button onClick={handleAddWatcher} type="primary">
            Share Access
          </Button>
        </div>

        {/* Display the existing watchers */}
        <ul className="watcher-list">
          {Object.entries(watchers).map(([email, attrs], index) => (
            <li key={index} className="watcher-list__item">
              <div className="watcher-details">
                <strong>{email}</strong> - 
                Updates: <span style={{ color: attrs.updates ? "green" : "red" }}>
                  {attrs.updates ? "✔" : "✘"}
                </span>, 
                Documents: <span style={{ color: attrs.documents ? "green" : "red" }}>
                  {attrs.documents ? "✔" : "✘"}
                </span>
              </div>
              <Button
                type="danger"
                onClick={() => handleRemoveWatcher(email)}
                className="remove-button"
                disabled={!attrs.updates && !attrs.documents} // Disable if both checkboxes are unchecked
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default WatcherPane;
